/**
 * Spend — money, and everything else you meter.
 *
 * Spend was over-indexed early on. Most of what an agent does costs nothing and still needs a
 * ceiling — tokens, tool calls, rows read, emails sent — and they all run through the same
 * ledger, which accepts any resource string. So this screen is not "money"; it is "limits",
 * and money is one of them.
 *
 * **A cap is opt-in, and no policy row IS metered-only.** There is no `limit = ∞` to set:
 * removing the row is the operation. Rendering an absurd ceiling instead would be a lie the
 * screen then keeps telling.
 */

import { useState } from "react";
import { Meter, State, fmtMoney, microToUsd, useAsync, usdToMicro } from "@authoxi/ui-shared";
import type { Api } from "../api";

export function Spend({ api }: { api: Api }) {
  const [period, setPeriod] = useState<"day" | "month">("day");
  const usage = useAsync(() => api.usage(period), [period]);
  const mandates = useAsync(() => api.mandates());
  const agents = useAsync(() => api.agents());

  const rows = usage.data?.rows ?? [];
  const money = rows.filter((r) => r.resource === "inference_usd");
  const other = rows.filter((r) => r.resource !== "inference_usd");
  const wallets = (mandates.data ?? []).filter((m) => m.subject_kind === "agent" && m.status === "active");

  return (
    <>
      <div className="view-head">
        <span className="label">Spend</span>
        <h1>Money, and everything else you meter.</h1>
        <p>
          A budget is enforced at the moment authority is issued — over budget means no passport
          refresh. That is how an agent gets cut off without us sitting on your inference path.
        </p>
      </div>

      <div className="row" style={{ marginBottom: "var(--s4)" }}>
        {(["day", "month"] as const).map((p) => (
          <button
            key={p}
            className={`btn ${period === p ? "btn-go" : ""}`}
            data-testid={`period-${p}`}
            onClick={() => setPeriod(p)}
          >
            {p === "day" ? "Today" : "This month"}
          </button>
        ))}
      </div>

      {usage.loading && <State kind="loading" title="Reading the ledger…" />}
      {usage.error && <State kind="error" title="Could not read usage" detail={usage.error} />}

      <div className="card pad-lg">
        <p className="label">Inference</p>
        {money.length === 0 ? (
          <p className="sm muted" style={{ marginTop: "var(--s4)" }}>
            Nothing metered this period.
          </p>
        ) : (
          <div className="stack" style={{ marginTop: "var(--s4)" }}>
            {money.map((r) => (
              <Row key={`${r.agent_id}-${r.resource}`} api={api} row={r} onChange={() => void usage.reload()} />
            ))}
          </div>
        )}
      </div>

      {/* The half that had never been written to. The ledger has always accepted any resource
          string, and nothing had ever created a non-`inference_usd` row — so the screen that
          argues most of governance is not about money had nothing to show for it. */}
      <div className="card pad" style={{ marginTop: "var(--s4)" }}>
        <p className="label">Non-money limits</p>
        <p className="sm" style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
          Most of what an agent does costs nothing and still needs a ceiling — tool calls, rows
          read, emails sent. They run through the same ledger and the same atomic guard.
        </p>
        {other.length === 0 ? (
          <p className="sm muted" style={{ marginTop: "var(--s4)" }} data-testid="no-other-limits">
            None set. Any resource string works: <span className="mono">mcp_calls</span>,{" "}
            <span className="mono">rows_read</span>.
          </p>
        ) : (
          <div className="stack" style={{ marginTop: "var(--s4)" }} data-testid="other-limits">
            {other.map((r) => (
              <Row key={`${r.agent_id}-${r.resource}`} api={api} row={r} onChange={() => void usage.reload()} />
            ))}
          </div>
        )}
      </div>

      <div className="rule" style={{ marginTop: "var(--s7)" }}>
        <span className="label">Spend authority</span>
      </div>
      <p className="sm muted" style={{ marginBottom: "var(--s4)" }}>
        {wallets.length} of {agents.data?.length ?? 0} agents may move money. That ratio is the
        design working, not a gap.
      </p>
      <div className="card pad">
        {wallets.length === 0 ? (
          <p className="sm muted">No agent holds a money mandate.</p>
        ) : (
          <div className="stack">
            {wallets.map((m) => (
              <div key={m.mandate_id}>
                <div className="between" style={{ marginBottom: "var(--s2)" }}>
                  <span className="mono sm">{m.subject_id}</span>
                  <span className="sm muted">
                    {fmtMoney(m.spent, m.currency)} of {fmtMoney(m.budget, m.currency)}
                  </span>
                </div>
                <Meter used={Number(m.spent)} limit={Number(m.budget)} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function Row({
  api,
  row,
  onChange,
}: {
  api: Api;
  row: { agent_id: string; resource: string; used: number; limit: number | null };
  onChange: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const isMoney = row.resource === "inference_usd";
  const fmt = (n: number) => (isMoney ? fmtMoney(microToUsd(n)) : n.toLocaleString());

  return (
    <div data-testid="usage-row">
      <div className="between" style={{ marginBottom: "var(--s2)" }}>
        <span className="mono sm">
          {row.agent_id}
          {!isMoney && <span className="muted"> · {row.resource}</span>}
        </span>
        <span className="sm muted">
          {row.limit === null ? (
            <>
              {fmt(row.used)} · <span className="pill">metered only</span>
            </>
          ) : (
            `${fmt(row.used)} / ${fmt(row.limit)}`
          )}
        </span>
      </div>
      <Meter used={row.used} limit={row.limit} />
      <div className="row" style={{ marginTop: "var(--s2)" }}>
        {editing ? (
          <>
            <input
              className="input"
              placeholder={isMoney ? "cap in dollars" : "cap in units"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="cap-input"
            />
            <button
              className="btn btn-go"
              data-testid="cap-save"
              onClick={async () => {
                await api.setUsagePolicy({
                  agent_id: row.agent_id,
                  resource: row.resource,
                  limit_units: isMoney ? usdToMicro(Number(value)) : Math.round(Number(value)),
                });
                setEditing(false);
                onChange();
              }}
            >
              Set cap
            </button>
            <button className="btn btn-quiet" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-quiet" onClick={() => setEditing(true)}>
              {row.limit === null ? "Set a cap" : "Change cap"}
            </button>
            {row.limit !== null && (
              <button
                className="btn btn-quiet"
                data-testid="cap-clear"
                onClick={async () => {
                  // Removing the row IS the operation. "No policy" is the ledger's own encoding
                  // of metered-only; there is no infinity to set.
                  await api.clearUsagePolicy(row.agent_id, row.resource);
                  onChange();
                }}
              >
                Remove cap (meter only)
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
